<template>
  <AeCard class="trap-card">
    <!-- Header trappola -->
    <template #header>
      <div class="tc-header">
        <div class="tc-title">
          <span class="tc-name">{{ trap.name }}</span>
          <span class="tc-chips">
            <span class="tc-chip">{{ TRAP_TYPES[trap.type] || trap.type }}</span>
            <span v-if="trap.pest" class="tc-chip tc-chip--pest">{{ trap.pest }}</span>
          </span>
        </div>
        <div class="tc-header-actions">
          <AeButton v-if="trap.automatic" variant="ghost" size="small" :loading="syncing" @click="syncTrapview()" :title="t('trapCard.syncButton')">
            <AeIcon name="RefreshCw" :size="14" />
          </AeButton>
          <AeButton variant="ghost" size="small" @click="$emit('edit-trap', trap)" :title="t('trapCard.editButton')">
            <AeIcon name="edit" :size="14" />
          </AeButton>
          <AeButton
            v-if="canDelete && !loading && obs.length === 0"
            variant="ghost" size="small"
            @click="$emit('delete-trap', trap)"
            :title="t('trapCard.deleteButton')"
          >
            <AeIcon name="Trash2" :size="14" />
          </AeButton>
        </div>
      </div>
    </template>

    <p v-if="syncError" class="tc-sync-error">{{ syncError }}</p>

    <!-- 0 rilevamenti -->
    <div v-if="!loading && obs.length === 0" class="tc-empty">
      <AeIcon name="AlertCircle" :size="28" class="tc-empty-icon" />
      <p v-if="trap.automatic">{{ t('trapCard.noAutoData') }}</p>
      <template v-else>
        <p>{{ t('trapCard.noObservations') }}</p>
        <AeButton variant="primary" size="small" @click="openAdd">{{ t('trapCard.addObservation') }}</AeButton>
      </template>
    </div>

    <!-- 1 rilevamento: solo tabella -->
    <div v-else-if="!loading && obs.length === 1" class="tc-single">
      <div v-if="!trap.automatic" class="tc-toolbar">
        <AeButton variant="primary" size="small" @click="openAdd">{{ t('trapCard.add') }}</AeButton>
      </div>
      <table class="tc-table">
        <thead><tr><th>{{ t('trapCard.date') }}</th><th>{{ t('trapCard.catches') }}</th><th>{{ t('trapCard.notes') }}</th><th></th></tr></thead>
        <tbody>
          <tr v-for="o in obs" :key="o.id_observation">
            <td>{{ o.date || '—' }}</td>
            <td class="tc-td-num">{{ o.trap ?? '—' }}</td>
            <td class="tc-td-note">{{ o.notes || '' }}</td>
            <td class="tc-td-actions">
              <AeButtonGroup size="small">


                <AeButton v-if="o.images?.length" variant="ghost" @click="openImage(o)" :title="t('trapCard.showImage')"><AeIcon name="Image" :size="13" /></AeButton>
                <AeButton v-if="!trap.automatic" variant="ghost" @click="openEdit(o)" :title="t('trapCard.edit')"><AeIcon name="edit" :size="13" /></AeButton>
                <AeButton variant="ghost" @click="doDelete(o)" :title="t('trapCard.delete')"><AeIcon name="Trash2" :size="13" /></AeButton>
              </AeButtonGroup>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- 2+ rilevamenti: tab grafico + dati -->
    <template v-else-if="!loading && obs.length >= 2">
      <div v-if="!trap.automatic" class="tc-toolbar">
        <AeButton variant="primary" size="small" @click="openAdd">{{ t('trapCard.add') }}</AeButton>
      </div>
      <AeTabPanel :tabs="tabs" v-model="activeTab">
        <template #grafico>
          <AeChart type="line" :data="chartData" :options="chartOptions" :height="220" />
        </template>
        <template #dati>
          <table class="tc-table">
            <thead><tr><th>{{ t('trapCard.date') }}</th><th>{{ t('trapCard.catches') }}</th><th>{{ t('trapCard.notes') }}</th><th></th></tr></thead>
            <tbody>
              <tr v-for="o in obs" :key="o.id_observation">
                <td>{{ o.date || '—' }}</td>
                <td class="tc-td-num">{{ o.trap ?? '—' }}</td>
                <td class="tc-td-note">{{ o.notes || '' }}</td>
                <td class="tc-td-actions">
                  <AeButtonGroup size="small">
                    <AeButton v-if="o.images?.length" variant="ghost" @click="openImage(o)" :title="t('trapCard.showImage')"><AeIcon name="Image" :size="13" /></AeButton>
                    <AeButton variant="ghost" @click="openEdit(o)" :title="t('trapCard.edit')"><AeIcon name="edit" :size="13" /></AeButton>
                    <AeButton variant="ghost" @click="doDelete(o)" :title="t('trapCard.delete')"><AeIcon name="Trash2" :size="13" /></AeButton>
                  </AeButtonGroup>
                </td>
              </tr>
            </tbody>
          </table>
        </template>
      </AeTabPanel>
    </template>

    <div v-if="loading" class="tc-loading">
      <div class="tc-spinner"></div>
    </div>

    <!-- Modal inserimento/modifica -->
    <OperationObservationModal
      v-model="showObsModal"
      :title="editingObs ? t('trapCard.editObservationTitle') : t('trapCard.newObservationTitle')"
      type="observation"
      :id-farm-center="trap.id_farm_center"
      :id-season="idSeason"
      :pre-selected-type="TRAP_SCHEMA_ID"
      :pre-selected-fields="trapFieldIds"
      :pre-selected-trap-id="trap.id_trap"
      :edit-object="editingObs"
      @saved="handleSaved"
      @cancel="showObsModal = false"
    />

    <!-- Modal immagine cattura -->
    <ImageZoomModal v-model="showImageModal" :items="imageItems" :start-index="imageStartIndex" />
  </AeCard>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDbStore } from '../stores/dbStore.js'
import { api } from '@/services/api.js'
import { AeButton, AeButtonGroup, AeCard, AeChart, AeIcon, AeTabPanel } from '@aevue/components'
import OperationObservationModal from './OperationObservationModal.vue'
import ImageZoomModal from './ImageZoomModal.vue'

const { t } = useI18n()

const props = defineProps({
  trap:        { type: Object, required: true },
  idSeason:    { type: String, default: null },
  canDelete:   { type: Boolean, default: true },
})

const emit = defineEmits(['edit-trap', 'delete-trap', 'refreshed'])

const dbStore    = useDbStore()
const obs        = ref([])
const loading    = ref(false)
const syncing    = ref(false)
const syncError  = ref('')
const showObsModal = ref(false)
const editingObs   = ref(null)
const activeTab    = ref('grafico')
const showImageModal  = ref(false)
const imageStartIndex = ref(0)

// Elenco catture con immagine, nello stesso ordine mostrato in tabella (obs è già ordinato)
const imageItems = computed(() =>
  obs.value
    .filter(o => o.images?.length)
    .map(o => ({ guid: o.images[0], date: o.date, trap: o.trap, id_observation: o.id_observation }))
)

const TRAP_SCHEMA_ID = 1

const TRAP_TYPES = computed(() => ({
  cromo:   t('trapCard.typeCromo'),
  pheno:   t('trapCard.typePheno'),
  flytrap: t('trapCard.typeFlytrap'),
  other:   t('trapCard.typeOther')
}))

const tabs = computed(() => [
  { key: 'grafico', label: t('trapCard.tabChart') },
  { key: 'dati',    label: t('trapCard.tabData') },
])

// Campi monitorati dalla trappola (selezionati direttamente in TrapUpsert)
const trapFieldIds = computed(() => props.trap.id_fields || [])

// ── Grafico ───────────────────────────────────────────────────────────────────
const chartData = computed(() => {
  const sorted = [...obs.value]
    .filter(o => o.trap != null && o.trap !== '' && !isNaN(parseFloat(o.trap)))
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
  const labels = sorted.map(o =>
    o.date ? new Date(o.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : '—'
  )
  return {
    labels,
    datasets: [{
      label: props.trap.name,
      data: sorted.map(o => parseFloat(o.trap) || 0),
      borderColor: '#4299e1',
      backgroundColor: '#4299e122',
      tension: 0.3,
      pointRadius: 4,
      spanGaps: false
    }]
  }
})

const chartOptions = computed(() => ({
  responsive: true,
  scales: { y: { beginAtZero: true, title: { display: true, text: t('trapCard.chartYAxis') } } },
  plugins: { legend: { display: false } }
}))

// ── Azioni ────────────────────────────────────────────────────────────────────
function openAdd() {
  editingObs.value = null
  showObsModal.value = true
}

function openEdit(o) {
  editingObs.value = o
  showObsModal.value = true
}

function openImage(o) {
  const idx = imageItems.value.findIndex(it => it.id_observation === o.id_observation)
  if (idx === -1) return
  imageStartIndex.value = idx
  showImageModal.value = true
}

async function doDelete(o) {
  await dbStore.delete('observation', o.id_observation)
  await loadData()
  emit('refreshed')
}

async function handleSaved() {
  showObsModal.value = false
  editingObs.value = null
  await loadData()
  emit('refreshed')
}

// ── Sync Trapview ─────────────────────────────────────────────────────────────
async function syncTrapview() {
  syncing.value   = true
  syncError.value = ''

  const idFarmCenter = props.trap.id_farm_center
  const externalId   = props.trap.external_id
  const res = await api.get(`/api/v2/admin/weather/trapview_pest_count/${idFarmCenter}/${externalId}`)
  syncing.value = false
  if (!res.ok) { syncError.value = res.message || t('trapCard.syncErrorDefault'); return }

  const items = Array.isArray(res.data) ? res.data : []
  for (const item of items) {
    const pc = item.pestCount
    if (!pc || pc.newPests === undefined) continue

    const date     = pc.date.split('T')[0]
    const newPests = pc.newPests
    const existing = obs.value.find(o => o.date === date)

    if (existing) {
      await dbStore.update('observation', existing.id_observation, { trap: newPests })
    } else {
      await dbStore.insert('observation', {
        id_farm_center:   idFarmCenter,
        id_season:        props.trap.id_season,
        id_trap:          props.trap.id_trap,
        id_survey_schema: TRAP_SCHEMA_ID,
        id_fields:        trapFieldIds.value,
        id_field:         trapFieldIds.value[0] ?? null,
        date,
        trap:             newPests,
        auto:             true,
      })
    }
  }

  await loadData()
}

// ── Load ──────────────────────────────────────────────────────────────────────
async function loadData() {
  loading.value = true
  try {
    const obsRes = await dbStore.select('observation', { id_farm_center: props.trap.id_farm_center })
    obs.value = (obsRes?.data || [])
      .filter(o => String(o.id_survey_schema) === '1' && o.id_trap === props.trap.id_trap)
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
  } finally {
    loading.value = false
  }
}

onMounted(loadData)
watch(() => props.trap.id_trap, loadData)
</script>

<style scoped>
.trap-card { display: flex; flex-direction: column; gap: 0; }

/* Header */
.tc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.tc-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  min-width: 0;
}
.tc-name {
  font-weight: 600;
  font-size: 0.95rem;
  color: var(--text-color);
}
.tc-chips { display: flex; gap: 0.3rem; flex-wrap: wrap; }
.tc-chip {
  font-size: 0.72rem;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--ae-surface, rgba(0,0,0,0.06));
  color: var(--text-secondary, #718096);
  white-space: nowrap;
}
.tc-chip--pest {
  background: rgba(237, 137, 54, 0.12);
  color: #c05621;
}
.tc-header-actions { flex-shrink: 0; }

/* Toolbar */
.tc-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.6rem;
}

/* Empty */
.tc-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  padding: 1.25rem 0.5rem;
  text-align: center;
}
.tc-empty-icon { color: var(--text-secondary, #a0aec0); }
.tc-empty p { margin: 0; font-size: 0.88rem; color: var(--text-secondary, #718096); }

/* Single */
.tc-single { display: flex; flex-direction: column; gap: 0.5rem; }

/* Table */
.tc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}
.tc-table th {
  text-align: left;
  padding: 0.35rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-secondary, #718096);
  border-bottom: 2px solid var(--ae-border, rgba(0,0,0,0.1));
}
.tc-table td {
  padding: 0.4rem 0.6rem;
  border-bottom: 1px solid var(--ae-border, rgba(0,0,0,0.06));
  vertical-align: middle;
}
.tc-td-num  { font-weight: 600; width: 70px; }
.tc-td-note { color: var(--text-secondary, #718096); font-size: 0.82rem; max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tc-td-actions { width: 72px; text-align: right; white-space: nowrap; }

.tc-sync-error { font-size: 0.8rem; color: #c53030; margin: 4px 0 0; }

/* Loading */
.tc-loading { display: flex; justify-content: center; padding: 1rem; }
.tc-spinner {
  width: 1.25rem; height: 1.25rem;
  border: 2px solid var(--ae-border, #eee);
  border-top-color: var(--primary-color, #4299e1);
  border-radius: 50%;
  animation: tc-spin 0.7s linear infinite;
}
@keyframes tc-spin { to { transform: rotate(360deg); } }
</style>
